import { path } from 'ramda'
import { isObject } from '@/utils'
import { Filter as PlacementFilter, SortItems } from '@kvass/custom-fields/src/utils.js'

const transformCondtion = (newKey, oldKey, condition) => {
  return Object.fromEntries(
    Object.entries(condition).map(([key, value]) => {
      const result = key.replace(oldKey, newKey)
      return [result, isObject(value) ? transformCondtion(newKey, oldKey, value) : value]
    }),
  )
}
const transFormComponents = (components, newKey, oldKey) => {
  return components.map(c => {
    const newComponent = structuredClone(c)
    const condition = path(['options', 'condition'], newComponent)
    if (condition) {
      newComponent.options.condition = transformCondtion(newKey, oldKey, condition)
    }
    if (newComponent.component instanceof Array) {
      return {
        ...newComponent,
        component: transFormComponents(newComponent.component, newKey, oldKey),
      }
    }
    return newComponent
  })
}
function getCustomFieldsFromLayout(layout = [], customFieldSchemas, placement) {
  const data = structuredClone(customFieldSchemas)
  placement = (placement instanceof Array ? placement : [placement]).filter(Boolean)

  const hasInitLayout = layout.length

  //add fields that has display.cms to true. Is added last to layout ->  placed in correct position based on options.location in @kvass/custum-fields
  const placeInsideLayout = data.filter(i => {
    if (layout.find(l => l.key === i.key)) return
    const block = path(['block'], i)
    if (!block) return
    return path(['display', 'cms'], block) === true
  })

  layout = [...layout, ...placeInsideLayout]
  if (!Array.isArray(layout)) layout = []

  const result = layout
    .map(field => {
      if (!field) return

      const schema = data.find(c => {
        const blockKey = path(['block', 'key'], c) || c.key
        return [path(['data', 'reference'], field), field.key].includes(blockKey)
      })

      if (!schema) return

      let component = schema.component
      let options = schema.options || {}

      //remove location on fields that has no strict cms display and layout is defined
      if (path(['block', 'display', 'cms'], field) !== true && hasInitLayout) {
        options.location = null

        //remove settings fields in fields that is a copy from another field
        const isCopy = path(['data', 'reference'], field)
        if (isCopy && path(['block', 'added', 'remove', 'length'], schema)) {
          const newItem = path(['block', 'added', 'remove'], schema).reduce(
            (acc, currKey) => {
              acc.settings = (acc.settings || schema.settings || '').split(',').reduce((accS, currS) => {
                const [component, key, label] = currS.split(':')
                if (key === currKey) return accS
                return `${accS ? `${accS},` : ''}${currS}`
              }, '')

              acc.settingsLayout = (acc.settingsLayout || options.settingsLayout || '')
                .split('|')
                .reduce((accSL, currSL) => {
                  if (currSL.includes(currKey)) return accSL
                  return `${accSL ? `${accSL} | ` : ''}${currSL}`
                }, '')

              return acc
            },
            {
              settings: '',
              settingsLayout: '',
            },
          )

          schema.settings = newItem.settings

          options.settingsLayout = newItem.settingsLayout
        }
      }

      //transform condition with new key
      if (path(['condition'], options)) {
        options.condition = transformCondtion(field.key, schema.key, options.condition)
      }

      //tranform condition on each component with new key
      if (schema.component instanceof Array) {
        component = transFormComponents(schema.component, field.key, schema.key)
      }
      return {
        ...structuredClone(schema),
        options: structuredClone(options),
        component,
        key: field.key,
      }
    })
    .filter(Boolean)

  //place fields with a defined block.order at the given position (1-based). Others keep their relative order.
  const ordered = []
  const rest = []
  result.forEach(item => {
    const order = path(['block', 'display', 'order'], item)
    if (typeof order === 'number') ordered.push({ item, order })
    else rest.push(item)
  })

  ordered
    .sort((a, b) => a.order - b.order)
    .forEach(({ item, order }) => {
      const index = Math.min(Math.max(order - 1, 0), rest.length)
      rest.splice(index, 0, item)
    })

  //filter out duplicates by key, keeping the first occurrence
  const seen = new Set()
  return rest.filter(item => {
    if (seen.has(item.key)) return false
    seen.add(item.key)
    return true
  })
}

const getBlockKey = schema => path(['block', 'key'], schema) || schema.key

const hasNestedBlocks = components =>
  components instanceof Array && components.some(c => c.block || hasNestedBlocks(c.component))

//collects the subfields with block metadata inside a group. They inherit the placement of the top level group
function getNestedBlocks(schema, placement = schema.placement) {
  if (!(schema.component instanceof Array)) return []
  return schema.component.flatMap(c => {
    if (c.block) return [{ ...c, placement: path(['block', 'placement'], c) || placement }]
    return getNestedBlocks(c, placement)
  })
}

//the blocks the FieldBuilder lists for the placement, in their default order
function getBuilderBlocks(customFieldSchemas = [], placement) {
  const schemas = customFieldSchemas
    .flatMap(schema => [schema, ...getNestedBlocks(schema)])
    .map(schema => {
      return {
        ...schema,
        placement: path(['block', 'placement'], schema) || schema.placement,
        key: getBlockKey(schema),
        options: {
          ...(schema.options || {}),
          location: path(['block', 'location'], schema) || path(['options', 'location'], schema),
        },
      }
    })

  return SortItems(
    PlacementFilter(schemas, {
      placement: placement instanceof Array ? placement : [placement],
      keys: [],
    }),
  )
    .filter(cf => cf.block && path(['block', 'display', 'builder'], cf) !== false)
    .map(cf => ({ ...cf.block, key: cf.key, label: cf.block.label || cf.label }))
}

//layout entry that holds the block keys the builder listed when the layout was saved.
//the theme skips it, as it has no block with this key
const KNOWN_BLOCKS_KEY = '_known-blocks'

const isKnownBlocksEntry = field => path(['key'], field) === KNOWN_BLOCKS_KEY

//adds the entry with the block keys the builder lists to a layout that is saved
function withKnownBlocks(layout, customFieldSchemas, placement) {
  const keys = getBuilderBlocks(customFieldSchemas, placement).map(block => block.key)
  return [...(layout || []).filter(field => !isKnownBlocksEntry(field)), { key: KNOWN_BLOCKS_KEY, data: { keys } }]
}

//limit.min 0 -> added by default and can not be added again. limit.min 1 -> added by default and can be added again
const isDefaultBlock = block => [0, 1].includes(path(['limit', 'min'], block))

//a saved layout does not include blocks that are added in code afterwards -> add the default blocks that are new.
//new = not listed in the builder when the layout was saved, so blocks the user removed stay removed.
//layouts saved before the known keys were stored get all missing default blocks.
//each block is placed after the closest block before it in the default order. Without a saved layout the builder defaults are used
function addMissingBlocks(layout, customFieldSchemas, placement) {
  if (!(layout instanceof Array)) return layout

  const knownKeys = path(['data', 'keys'], layout.find(isKnownBlocksEntry))
  const result = layout.filter(field => !isKnownBlocksEntry(field))
  if (!result.length && !knownKeys) return result

  const indexOf = key => {
    let index = -1
    result.forEach((field, i) => {
      if ([field.key, path(['data', 'reference'], field)].includes(key)) index = i
    })
    return index
  }
  const isNew = key => !(knownKeys instanceof Array) || !knownKeys.includes(key)

  const blocks = getBuilderBlocks(customFieldSchemas, placement)
  blocks.forEach((block, index) => {
    if (!isDefaultBlock(block) || ~indexOf(block.key) || !isNew(block.key)) return
    if (block.location === 'last') return result.push({ key: block.key, data: {} })

    const previous = blocks
      .slice(0, index)
      .reverse()
      .find(b => ~indexOf(b.key))
    result.splice(previous ? indexOf(previous.key) + 1 : 0, 0, { key: block.key, data: {} })
  })

  return result
}

//removes the subfields with block metadata that are not in the layout and sorts the rest by the layout.
//the sorted subfields take the positions of the block subfields in options.layout -> other subfields keep their position.
function applyLayoutToComponents(components, options = {}, order) {
  const isBlock = c => Boolean(c.block) || hasNestedBlocks(c.component)

  const items = components.map(c => {
    if (c.block) return order.has(getBlockKey(c)) ? { schema: c, order: order.get(getBlockKey(c)) } : null
    if (!hasNestedBlocks(c.component)) return { schema: c }

    const nested = applyLayoutToComponents(c.component, c.options, order)
    if (!nested.component.length) return null
    return { schema: { ...c, component: nested.component, options: nested.options }, order: nested.order }
  })

  const sorted = items.filter((item, index) => item && isBlock(components[index])).sort((a, b) => a.order - b.order)

  //block positions in the order of options.layout. Subfields not in the layout keep the component order after them
  const layoutKeys = (options.layout || '')
    .split(/[|\s]+/)
    .map(col => col.split(':')[0])
    .filter(Boolean)
  const position = c => (layoutKeys.includes(c.key) ? layoutKeys.indexOf(c.key) : layoutKeys.length)
  const positions = components.filter(isBlock).sort((a, b) => position(a) - position(b))

  //map each block position to the subfield that is placed there
  const slots = new Map(positions.map((c, index) => [c.key, sorted[index] && sorted[index].schema.key]))
  const result = components
    .map((c, index) => {
      if (!isBlock(c)) return items[index]
      return sorted.find(item => item.schema.key === slots.get(c.key))
    })
    .filter(Boolean)

  let layout = options.layout
  if (layout) {
    layout = layout
      .split('|')
      .map(row =>
        row
          .split(' ')
          .filter(Boolean)
          .map(col => {
            const [key, ...rest] = col.split(':')
            if (!slots.has(key)) return col
            return slots.get(key) && [slots.get(key), ...rest].join(':')
          })
          .filter(Boolean)
          .join(' '),
      )
      .filter(Boolean)
      .join(' | ')
  }

  const orders = sorted.map(i => i.order)
  return {
    component: result.map(i => i.schema),
    options: { ...options, ...(layout !== undefined && { layout }) },
    order: orders.length ? Math.min(...orders) : undefined,
  }
}

//placement is a string, an array or an object { resource, step } (custom fields from the server) -> same as Filter in @kvass/custom-fields
function getPlacements(schema) {
  const placement = schema.placement
  if (isObject(placement))
    return placement.step ? [`${placement.resource}:${placement.step}`, placement.step] : [placement.resource]
  return placement instanceof Array ? placement : [placement]
}

//keeps the fields without block metadata (standard fields) and replaces the block fields in placement with the layout
function getCustomFieldsWithLayout(layout, customFieldSchemas, placement) {
  placement = placement instanceof Array ? placement : [placement]
  const inPlacement = schema => getPlacements(schema).some(p => placement.includes(p))

  //a saved layout can be empty apart from the known blocks entry -> it is still a saved layout
  const hasLayout = Boolean(layout && layout.length)
  layout = addMissingBlocks(layout, customFieldSchemas, placement)

  const blocks = customFieldSchemas.filter(schema => schema.block && inPlacement(schema))
  const blockKeys = blocks.map(getBlockKey)

  //subfields with block metadata inside a group are filtered and sorted within the group. Without a layout all subfields are kept
  const order = new Map((layout || []).map((field, index) => [field.key, index]))
  const applyNestedLayout = schema => {
    if (!hasLayout || !inPlacement(schema) || !hasNestedBlocks(schema.component)) return schema
    const { component, options } = applyLayoutToComponents(schema.component, schema.options, order)
    if (!component.length) return
    return { ...schema, component, options }
  }

  return [
    //a field can be defined both with and without block metadata (e.g. additional-sections) -> the layout decides
    ...customFieldSchemas
      .filter(schema => !blocks.includes(schema) && !(inPlacement(schema) && blockKeys.includes(schema.key)))
      .map(applyNestedLayout)
      .filter(Boolean),
    ...getCustomFieldsFromLayout(layout, blocks, placement),
  ]
}

export { getCustomFieldsFromLayout, getCustomFieldsWithLayout, getBuilderBlocks, addMissingBlocks, withKnownBlocks }
